type PollProps<T> = {
  /**
   * Any errors thrown inside `fn` will be caught and converted to a rejected promise.
   */
  fn: () => Promise<T>;
  until: (fnResult: T) => boolean;
  isSessionOutcomePolling?: boolean;
  maxAttempts?: number;
  interval?: number;
};

interface PollingIntervalChangeDetail {
  pollingInterval: number;
}

interface PollingIntervalChangeEvent
  extends CustomEvent<PollingIntervalChangeDetail> {
  detail: PollingIntervalChangeDetail;
}

export default async <T>({
  fn,
  until,
  isSessionOutcomePolling = false,
  maxAttempts = 0,
  interval = 1000,
}: PollProps<T>): Promise<T> => {
  let attempts = 0;
  let sessionOutcomeIntervalVal: number | undefined;
  let pollingTimeoutId:
    | ReturnType<typeof setTimeout>
    | undefined;
  let executePollResolve: (value: T) => void;
  let executePollReject: (reason: any) => void;
  let isCleanedUp = false;
  let eventListenerAdded = false;

  // Forward declare functions to avoid ordering issues
  let executePoll: (
    resolve: (value: T) => void,
    reject: (reason: any) => void,
  ) => void;
  let handlePollingIntervalChange: (event: Event) => void;

  const cleanup = () => {
    if (isCleanedUp) return;
    isCleanedUp = true;

    if (pollingTimeoutId !== undefined) {
      clearTimeout(pollingTimeoutId); // Fixed: use clearTimeout instead of clearInterval
      pollingTimeoutId = undefined;
    }

    if (eventListenerAdded) {
      try {
        document.removeEventListener(
          'ccg_pollingIntervalChange',
          handlePollingIntervalChange,
        );
        eventListenerAdded = false;
      } catch (error) {
        // Log cleanup errors to aid debugging
        console.error('Error during cleanup (removing event listener):', error);
      }
    }
  };

  handlePollingIntervalChange = (event: Event) => {
    if (isCleanedUp) return;

    // Fixed: Type-safe event handling without unsafe casting
    if (
      event instanceof CustomEvent &&
      event.detail &&
      typeof event.detail.pollingInterval === 'number'
    ) {
      sessionOutcomeIntervalVal = event.detail.pollingInterval;

      if (pollingTimeoutId) {
        // to eliminate double polling when switching pages
        clearTimeout(pollingTimeoutId); // Fixed: use clearTimeout instead of clearInterval
        pollingTimeoutId = undefined;
        executePoll(executePollResolve, executePollReject);
      }
    }
  };

  executePoll = (
    resolve: (value: T) => void,
    reject: (reason: any) => void,
  ) => {
    if (isCleanedUp) return;

    pollingTimeoutId = undefined; // set undefined to eliminate double polling with slow internet
    executePollResolve = resolve;
    executePollReject = reject;

    // Fixed: Properly handle async execution and errors
    (async () => {
      try {
        const result: T = await fn();
        attempts += 1;

        if (isCleanedUp) return;

        if (until(result)) {
          cleanup();
          resolve(result);
        } else if (maxAttempts && attempts === maxAttempts) {
          cleanup();
          reject(new Error('Exceeded max attempts'));
        } else {
          pollingTimeoutId = setTimeout(
            executePoll,
            isSessionOutcomePolling && sessionOutcomeIntervalVal
              ? sessionOutcomeIntervalVal
              : interval,
            resolve,
            reject,
          ) as unknown as ReturnType<typeof setTimeout>;
        }
      } catch (error) {
        cleanup();
        reject(error);
      }
    })().catch((error) => {
      // Handle any uncaught errors from the async IIFE
      cleanup();
      reject(error);
    });

    // Fixed: Only add event listener once if session outcome polling is enabled
    if (
      isSessionOutcomePolling &&
      !eventListenerAdded &&
      !isCleanedUp
    ) {
      document.addEventListener(
        'ccg_pollingIntervalChange',
        handlePollingIntervalChange,
      );
      eventListenerAdded = true;
    }
  };

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      executePoll(resolve, reject);
    }, interval);
    pollingTimeoutId = timeoutId;
  });
};

export type {
  PollProps,
  PollingIntervalChangeEvent,
  PollingIntervalChangeDetail,
};
